import React, { useState } from 'react'
import type {
	SelectTabData,
	SelectTabEvent
} from '@fluentui/react-components'
import {
	Card,
	Text,
	Tab,
	TabList,
	Badge,
	Link,
	makeStyles,
	tokens
} from '@fluentui/react-components'
import {
	AppsRegular,
	BotRegular,
	BuildingShopRegular,
	ShareRegular,
	TabDesktopRegular,
	TabsRegular
} from '@fluentui/react-icons'
import { TeamsTab } from './TeamsTab'
import { teamsIntegrationContent } from './content'
import { useCommonStyles } from '../common/styles'

const useStyles = makeStyles({
	featuresGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
		gap: '1.5rem',
		marginBottom: '2rem'
	},
	featureCard: {
		padding: '2rem',
		height: '250px',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		textAlign: 'center',
		transition: 'transform 0.2s ease, box-shadow 0.2s ease',
		':hover': {
			transform: 'translateY(-2px)',
			boxShadow: tokens.shadow8
		}
	},
	deploymentSection: {
		padding: '2rem',
		backgroundColor: tokens.colorNeutralBackground2,
		borderRadius: tokens.borderRadiusMedium,
		marginTop: '2rem'
	},
	deploymentSteps: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
		gap: '1rem',
		marginTop: '1.5rem'
	},
	stepCard: {
		padding: '1.5rem',
		textAlign: 'center',
		border: `2px solid ${tokens.colorNeutralStroke2}`,
		borderRadius: tokens.borderRadiusMedium
	},
})

type TabValue = 'overview' | 'demo' | 'deployment'

export const TeamsIntegration: React.FC = () => {
	const styles = useStyles()
	const commonStyles = useCommonStyles()
	const [selectedTab, setSelectedTab] = useState<TabValue>('demo')

	const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
		setSelectedTab(data.value as TabValue)
	}

	const renderOverview = () => (
		<div>
			<Card style={{ marginBottom: '2rem', padding: '2rem' }}>
				<Text size={500} weight="semibold" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
					<TabsRegular style={{ marginRight: '0.5rem', color: tokens.colorPaletteBlueBorderActive }} />
					{teamsIntegrationContent.overview.title}
				</Text>
				<Text style={{ marginBottom: '1rem' }}>
					{teamsIntegrationContent.overview.description}
				</Text>
			</Card>

			<div className={styles.featuresGrid}>
				{teamsIntegrationContent.features.map((feature, index) => (
					<Card key={index} className={styles.featureCard}>
						<div>
							{index === 0
								? <TabDesktopRegular style={{ fontSize: '3rem', color: tokens.colorPaletteBlueBorderActive }} />
								: index === 1
									? <BotRegular style={{ fontSize: '3rem', color: tokens.colorPaletteGreenBorderActive }} />
									: <ShareRegular style={{ fontSize: '3rem', color: tokens.colorPalettePurpleBorderActive }} />
							}
							<Text size={500} weight="semibold" style={{ margin: '1rem 0 0.5rem 0' }}>
								{feature.title}
							</Text><br />
							<Text size={300}>{feature.description}</Text>
						</div>
						<div className={commonStyles.techBadges}>
							{feature.technologies.map((tech, techIndex) => (
								<Badge key={techIndex} appearance="outline">
									{tech}
								</Badge>
							))}
						</div>
					</Card>
				))}
			</div>

			<div className={styles.deploymentSection}>
				<Text size={600} weight="semibold" style={{ marginBottom: '1rem' }}>
					{teamsIntegrationContent.deployment.title}
				</Text><br />
				<Text style={{ marginBottom: '1.5rem' }}>
					{teamsIntegrationContent.deployment.description}
				</Text>

				<div className={styles.deploymentSteps}>
					{teamsIntegrationContent.deployment.steps.map((step, index) => (
						<div key={index} className={styles.stepCard}>
							<div style={{ fontSize: '2rem', color: tokens.colorPaletteBlueBorderActive, marginBottom: '0.5rem' }}>
								{index === 0 ? <AppsRegular /> : index === 1 ? <BuildingShopRegular /> : <TabsRegular />}
							</div>
							<Badge appearance="filled" color="brand" style={{ marginBottom: '0.5rem' }}>
								Step {step.step}
							</Badge>&nbsp;
							<Text size={400} weight="semibold" style={{ marginBottom: '0.5rem' }}>
								{step.title}
							</Text><br />
							<Text size={300}>{step.description}</Text>
						</div>
					))}
				</div>

				<div style={{ textAlign: 'center', marginTop: '2rem' }}>
					<Text size={300} style={{ marginBottom: '1rem' }}>
						Ready for Teams Store submission with:
					</Text>
					<div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
						{teamsIntegrationContent.deployment.submissionItems.map((item, index) => (
							<Badge key={index} appearance="filled" color="success">{item}</Badge>
						))}
					</div>
				</div>
			</div>
		</div>
	)

	const renderDeployment = () => (
		<div>
			<Card style={{ padding: '2rem', marginBottom: '2rem' }}>
				<Text size={600} weight="semibold" style={{ marginBottom: '1.5rem' }}>
					Teams Store Deployment Package
				</Text>

				<div style={{ marginBottom: '2rem' }}>
					<Text size={400} weight="semibold" style={{ marginBottom: '0.5rem' }}>
						App Manifest (manifest.json)
					</Text>
					<Text style={{ marginBottom: '1rem' }}>
						Complete Teams app manifest with metadata, permissions, tabs, and bot configuration.
					</Text>
					<Link href={teamsIntegrationContent.deployment.manifestUrl} target="_blank">
						View manifest.json →
					</Link>
				</div>

				<div style={{ marginBottom: '2rem' }}>
					<Text size={400} weight="semibold" style={{ marginBottom: '0.5rem' }}>
						{teamsIntegrationContent.deployment.revenue.title}
					</Text>
					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
						{teamsIntegrationContent.deployment.revenue.models.map((model, index) => (
							<div key={index} style={{ padding: '1rem', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium }}>
								<Text weight="semibold">{model.title}</Text>&nbsp;
								<Text size={300}>{model.description}</Text>
							</div>
						))}
					</div>
				</div>

				<div>
					<Text size={400} weight="semibold" style={{ marginBottom: '0.5rem' }}>
						{teamsIntegrationContent.deployment.market.title}
					</Text><br />
					<Text>
						{teamsIntegrationContent.deployment.market.description}
					</Text>
				</div>
			</Card>
		</div>
	)

	return (
		<div className={commonStyles.container}>
			<div className={commonStyles.tabContainer}>
				<TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
					<Tab value="overview">Overview</Tab>
					<Tab value="demo">Live Demo</Tab>
					<Tab value="deployment">Store Deployment</Tab>
				</TabList>

				<div className={commonStyles.tabContent}>
					{selectedTab === 'overview' && renderOverview()}
					{selectedTab === 'demo' && <TeamsTab />}
					{selectedTab === 'deployment' && renderDeployment()}
				</div>
			</div>
		</div >
	)
}

export default TeamsIntegration